import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useFactoryAddresses } from '../hooks/useFactoryAddresses';
import { DIRECT_SALE_ABI, AUCTION_SALE_ABI } from '../types/contracts';
import { makeContractCall, randomDelay, checkContractExists, makeSequentialContractCalls } from '../utils/contractUtils';

interface FactoryManagerProps {}

type FactoryTab = 'direct-sale' | 'auction-sale' | 'direct-sale-piece' | 'auction-sale-piece' | 'auction-sale-piece-reserve';

const FactoryManager: React.FC<FactoryManagerProps> = () => {
  const [activeTab, setActiveTab] = useState<FactoryTab>('direct-sale');
  const [directSaleFactoryInfo, setDirectSaleFactoryInfo] = useState<any>(null);
  const [auctionSaleFactoryInfo, setAuctionSaleFactoryInfo] = useState<any>(null);
  const [directSalePieceFactoryInfo, setDirectSalePieceFactoryInfo] = useState<any>(null);
  const [auctionSalePieceFactoryInfo, setAuctionSalePieceFactoryInfo] = useState<any>(null);
  const [auctionSalePieceReserveFactoryInfo, setAuctionSalePieceReserveFactoryInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);

  // Form states for direct sale factory
  const [directDevFeePercentage, setDirectDevFeePercentage] = useState('');
  const [directDevFeeRecipient, setDirectDevFeeRecipient] = useState('');
  const [directFakewhaleFeePercentage, setDirectFakewhaleFeePercentage] = useState('');
  const [directFakewhaleFeeRecipient, setDirectFakewhaleFeeRecipient] = useState('');

  // Form states for auction sale factory
  const [auctionDevFeePercentage, setAuctionDevFeePercentage] = useState('');
  const [auctionDevFeeRecipient, setAuctionDevFeeRecipient] = useState('');
  const [auctionFakewhaleFeePercentage, setAuctionFakewhaleFeePercentage] = useState('');
  const [auctionFakewhaleFeeRecipient, setAuctionFakewhaleFeeRecipient] = useState('');

  // Form states for direct sale piece factory
  const [directPieceDevFeePercentage, setDirectPieceDevFeePercentage] = useState('');
  const [directPieceDevFeeRecipient, setDirectPieceDevFeeRecipient] = useState('');
  const [directPieceFakewhaleFeePercentage, setDirectPieceFakewhaleFeePercentage] = useState('');
  const [directPieceFakewhaleFeeRecipient, setDirectPieceFakewhaleFeeRecipient] = useState('');

  // Form states for auction sale piece factory
  const [auctionPieceDevFeePercentage, setAuctionPieceDevFeePercentage] = useState('');
  const [auctionPieceDevFeeRecipient, setAuctionPieceDevFeeRecipient] = useState('');
  const [auctionPieceFakewhaleFeePercentage, setAuctionPieceFakewhaleFeePercentage] = useState('');
  const [auctionPieceFakewhaleFeeRecipient, setAuctionPieceFakewhaleFeeRecipient] = useState('');

  // Form states for auction sale piece reserve factory
  const [auctionPieceReserveDevFeePercentage, setAuctionPieceReserveDevFeePercentage] = useState('');
  const [auctionPieceReserveDevFeeRecipient, setAuctionPieceReserveDevFeeRecipient] = useState('');
  const [auctionPieceReserveFakewhaleFeePercentage, setAuctionPieceReserveFakewhaleFeePercentage] = useState('');
  const [auctionPieceReserveFakewhaleFeeRecipient, setAuctionPieceReserveFakewhaleFeeRecipient] = useState('');

  const { factoryAddresses, loading: factoryLoading, error: factoryError } = useFactoryAddresses();

  // Check wallet connection
  const checkWalletConnection = useCallback(async () => {
    if (!window.ethereum) {
      setWalletConnected(false);
      return false;
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        setWalletConnected(true);
        return true;
      } else {
        setWalletConnected(false);
        return false;
      }
    } catch (err) {
      setWalletConnected(false);
      return false;
    }
  }, []);

  // Check wallet connection on mount
  useEffect(() => {
    checkWalletConnection();
  }, [checkWalletConnection]);


  // Factory management ABI
  const factoryManagementABI = [
    "function devFeePercentage() external view returns (uint256)",
    "function devFeeRecipient() external view returns (address)",
    "function fakewhaleFeePercentage() external view returns (uint256)",
    "function fakewhaleFeeRecipient() external view returns (address)",
    "function owner() external view returns (address)",
    "function setDevFeeConfiguration(uint256 _devFeePercentage, address _devFeeRecipient) external",
    "function setFakewhaleFeeConfiguration(uint256 _fakewhaleFeePercentage, address _fakewhaleFeeRecipient) external"
  ];

  // Helper function to load a specific factory's information
  const loadSpecificFactoryInfo = useCallback(async (factoryType: FactoryTab) => {
    if (!factoryAddresses) {
      return;
    }

    // Check wallet connection first
    const isConnected = await checkWalletConnection();
    if (!isConnected) {
      setError('Please connect your wallet to load factory information.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if MetaMask is available
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed. Please install MetaMask to interact with contracts.');
      }

      // Use injected wallet provider
      const provider = new ethers.BrowserProvider(window.ethereum);
      
      // Check network - use chainId from factory addresses config
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);
      const expectedChainId = factoryAddresses.chainId;
      
      if (chainId !== expectedChainId) {
        throw new Error(`Please switch to the correct network (Chain ID: ${expectedChainId}). Current network: ${chainId}`);
      }

      let factoryAddress: string | undefined;
      let setFactoryInfo: (info: any) => void;

      switch (factoryType) {
        case 'direct-sale':
          factoryAddress = factoryAddresses.factories.directSaleFactory;
          setFactoryInfo = setDirectSaleFactoryInfo;
          break;
        case 'auction-sale':
          factoryAddress = factoryAddresses.factories.auctionSaleFactory;
          setFactoryInfo = setAuctionSaleFactoryInfo;
          break;
        case 'direct-sale-piece':
          factoryAddress = factoryAddresses.factories.directSalePieceFactory;
          setFactoryInfo = setDirectSalePieceFactoryInfo;
          break;
        case 'auction-sale-piece':
          factoryAddress = factoryAddresses.factories.auctionSalePieceFactory;
          setFactoryInfo = setAuctionSalePieceFactoryInfo;
          break;
        case 'auction-sale-piece-reserve':
          factoryAddress = factoryAddresses.factories.auctionSalePieceReserveFactory;
          setFactoryInfo = setAuctionSalePieceReserveFactoryInfo;
          break;
        default:
          throw new Error(`Unknown factory type: ${factoryType}`);
      }

      if (!factoryAddress) {
        throw new Error(`Factory address not found for ${factoryType}`);
      }

      const factory = new ethers.Contract(
        factoryAddress,
        factoryManagementABI,
        provider
      );

      console.log(`Loading ${factoryType} factory info from:`, factoryAddress);

      // Check if contract exists
      const factoryExists = await checkContractExists(provider, factoryAddress);
      if (!factoryExists) {
        throw new Error(`No contract found at address: ${factoryAddress}`);
      }

      // Make calls sequentially with random delays to avoid rate limiting
      const [
        devFeePercentage,
        devFeeRecipient,
        fakewhaleFeePercentage,
        fakewhaleFeeRecipient,
        owner
      ] = await makeSequentialContractCalls(
        [
          () => factory.devFeePercentage(),
          () => factory.devFeeRecipient(),
          () => factory.fakewhaleFeePercentage(),
          () => factory.fakewhaleFeeRecipient(),
          () => factory.owner()
        ],
        [
          `get ${factoryType} dev fee percentage`,
          `get ${factoryType} dev fee recipient`,
          `get ${factoryType} fakewhale fee percentage`,
          `get ${factoryType} fakewhale fee recipient`,
          `get ${factoryType} owner`
        ]
      );

      setFactoryInfo({
        devFeePercentage: Number(devFeePercentage),
        devFeeRecipient: devFeeRecipient,
        fakewhaleFeePercentage: Number(fakewhaleFeePercentage),
        fakewhaleFeeRecipient: fakewhaleFeeRecipient,
        owner: owner
      });

    } catch (err) {
      console.error(`Error loading ${factoryType} factory info:`, err);
      setError(`Failed to load factory information: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [factoryAddresses, checkWalletConnection]);

  // Load factory info when tab changes (only if not already loaded)
  useEffect(() => {
    if (!factoryAddresses) return;

    // Check if we already have the info for this factory type
    let hasInfo = false;
    switch (activeTab) {
      case 'direct-sale':
        hasInfo = directSaleFactoryInfo !== null;
        break;
      case 'auction-sale':
        hasInfo = auctionSaleFactoryInfo !== null;
        break;
      case 'direct-sale-piece':
        hasInfo = directSalePieceFactoryInfo !== null;
        break;
      case 'auction-sale-piece':
        hasInfo = auctionSalePieceFactoryInfo !== null;
        break;
      case 'auction-sale-piece-reserve':
        hasInfo = auctionSalePieceReserveFactoryInfo !== null;
        break;
    }

    // Only load if we don't have the info yet
    if (!hasInfo) {
      loadSpecificFactoryInfo(activeTab);
    }
  }, [activeTab, factoryAddresses, loadSpecificFactoryInfo, directSaleFactoryInfo, auctionSaleFactoryInfo, directSalePieceFactoryInfo, auctionSalePieceFactoryInfo, auctionSalePieceReserveFactoryInfo]);

  // Update form fields when factory info loads
  useEffect(() => {
    if (directSaleFactoryInfo) {
      setDirectDevFeePercentage((directSaleFactoryInfo.devFeePercentage / 100).toString());
      setDirectDevFeeRecipient(directSaleFactoryInfo.devFeeRecipient);
      setDirectFakewhaleFeePercentage((directSaleFactoryInfo.fakewhaleFeePercentage / 100).toString());
      setDirectFakewhaleFeeRecipient(directSaleFactoryInfo.fakewhaleFeeRecipient);
    }
  }, [directSaleFactoryInfo]);

  useEffect(() => {
    if (auctionSaleFactoryInfo) {
      setAuctionDevFeePercentage((auctionSaleFactoryInfo.devFeePercentage / 100).toString());
      setAuctionDevFeeRecipient(auctionSaleFactoryInfo.devFeeRecipient);
      setAuctionFakewhaleFeePercentage((auctionSaleFactoryInfo.fakewhaleFeePercentage / 100).toString());
      setAuctionFakewhaleFeeRecipient(auctionSaleFactoryInfo.fakewhaleFeeRecipient);
    }
  }, [auctionSaleFactoryInfo]);

  useEffect(() => {
    if (directSalePieceFactoryInfo) {
      setDirectPieceDevFeePercentage((directSalePieceFactoryInfo.devFeePercentage / 100).toString());
      setDirectPieceDevFeeRecipient(directSalePieceFactoryInfo.devFeeRecipient);
      setDirectPieceFakewhaleFeePercentage((directSalePieceFactoryInfo.fakewhaleFeePercentage / 100).toString());
      setDirectPieceFakewhaleFeeRecipient(directSalePieceFactoryInfo.fakewhaleFeeRecipient);
    }
  }, [directSalePieceFactoryInfo]);

  useEffect(() => {
    if (auctionSalePieceFactoryInfo) {
      setAuctionPieceDevFeePercentage((auctionSalePieceFactoryInfo.devFeePercentage / 100).toString());
      setAuctionPieceDevFeeRecipient(auctionSalePieceFactoryInfo.devFeeRecipient);
      setAuctionPieceFakewhaleFeePercentage((auctionSalePieceFactoryInfo.fakewhaleFeePercentage / 100).toString());
      setAuctionPieceFakewhaleFeeRecipient(auctionSalePieceFactoryInfo.fakewhaleFeeRecipient);
    }
  }, [auctionSalePieceFactoryInfo]);

  useEffect(() => {
    if (auctionSalePieceReserveFactoryInfo) {
      setAuctionPieceReserveDevFeePercentage((auctionSalePieceReserveFactoryInfo.devFeePercentage / 100).toString());
      setAuctionPieceReserveDevFeeRecipient(auctionSalePieceReserveFactoryInfo.devFeeRecipient);
      setAuctionPieceReserveFakewhaleFeePercentage((auctionSalePieceReserveFactoryInfo.fakewhaleFeePercentage / 100).toString());
      setAuctionPieceReserveFakewhaleFeeRecipient(auctionSalePieceReserveFactoryInfo.fakewhaleFeeRecipient);
    }
  }, [auctionSalePieceReserveFactoryInfo]);

  const updateDirectSaleFactoryDevFee = async () => {
    if (!factoryAddresses?.factories.directSaleFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.directSaleFactory,
        factoryManagementABI,
        signer
      );

      const devFeePercentageBasisPoints = Math.round(parseFloat(directDevFeePercentage) * 100);
      const tx = await factory.setDevFeeConfiguration(devFeePercentageBasisPoints, directDevFeeRecipient);
      await tx.wait();

      setSuccess('Direct Sale Factory dev fee configuration updated successfully!');
      await loadSpecificFactoryInfo('direct-sale');
    } catch (err) {
      console.error('Error updating direct sale factory dev fee:', err);
      setError(`Failed to update dev fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDirectSaleFactoryFakewhaleFee = async () => {
    if (!factoryAddresses?.factories.directSaleFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.directSaleFactory,
        factoryManagementABI,
        signer
      );

      const fakewhaleFeePercentageBasisPoints = Math.round(parseFloat(directFakewhaleFeePercentage) * 100);
      const tx = await factory.setFakewhaleFeeConfiguration(fakewhaleFeePercentageBasisPoints, directFakewhaleFeeRecipient);
      await tx.wait();

      setSuccess('Direct Sale Factory fakewhale fee configuration updated successfully!');
      await loadSpecificFactoryInfo('direct-sale');
    } catch (err) {
      console.error('Error updating direct sale factory fakewhale fee:', err);
      setError(`Failed to update fakewhale fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAuctionSaleFactoryDevFee = async () => {
    if (!factoryAddresses?.factories.auctionSaleFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.auctionSaleFactory,
        factoryManagementABI,
        signer
      );

      const devFeePercentageBasisPoints = Math.round(parseFloat(auctionDevFeePercentage) * 100);
      const tx = await factory.setDevFeeConfiguration(devFeePercentageBasisPoints, auctionDevFeeRecipient);
      await tx.wait();

      setSuccess('Auction Sale Factory dev fee configuration updated successfully!');
      await loadSpecificFactoryInfo('auction-sale');
    } catch (err) {
      console.error('Error updating auction sale factory dev fee:', err);
      setError(`Failed to update dev fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAuctionSaleFactoryFakewhaleFee = async () => {
    if (!factoryAddresses?.factories.auctionSaleFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.auctionSaleFactory,
        factoryManagementABI,
        signer
      );

      const fakewhaleFeePercentageBasisPoints = Math.round(parseFloat(auctionFakewhaleFeePercentage) * 100);
      const tx = await factory.setFakewhaleFeeConfiguration(fakewhaleFeePercentageBasisPoints, auctionFakewhaleFeeRecipient);
      await tx.wait();

      setSuccess('Auction Sale Factory fakewhale fee configuration updated successfully!');
      await loadSpecificFactoryInfo('auction-sale');
    } catch (err) {
      console.error('Error updating auction sale factory fakewhale fee:', err);
      setError(`Failed to update fakewhale fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDirectSalePieceFactoryDevFee = async () => {
    if (!factoryAddresses?.factories.directSalePieceFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.directSalePieceFactory,
        factoryManagementABI,
        signer
      );

      const devFeePercentageBasisPoints = Math.round(parseFloat(directPieceDevFeePercentage) * 100);
      const tx = await factory.setDevFeeConfiguration(devFeePercentageBasisPoints, directPieceDevFeeRecipient);
      await tx.wait();

      setSuccess('Direct Sale Piece Factory dev fee configuration updated successfully!');
      await loadSpecificFactoryInfo('direct-sale-piece');
    } catch (err) {
      console.error('Error updating direct sale piece factory dev fee:', err);
      setError(`Failed to update dev fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDirectSalePieceFactoryFakewhaleFee = async () => {
    if (!factoryAddresses?.factories.directSalePieceFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.directSalePieceFactory,
        factoryManagementABI,
        signer
      );

      const fakewhaleFeePercentageBasisPoints = Math.round(parseFloat(directPieceFakewhaleFeePercentage) * 100);
      const tx = await factory.setFakewhaleFeeConfiguration(fakewhaleFeePercentageBasisPoints, directPieceFakewhaleFeeRecipient);
      await tx.wait();

      setSuccess('Direct Sale Piece Factory fakewhale fee configuration updated successfully!');
      await loadSpecificFactoryInfo('direct-sale-piece');
    } catch (err) {
      console.error('Error updating direct sale piece factory fakewhale fee:', err);
      setError(`Failed to update fakewhale fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAuctionSalePieceFactoryDevFee = async () => {
    if (!factoryAddresses?.factories.auctionSalePieceFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.auctionSalePieceFactory,
        factoryManagementABI,
        signer
      );

      const devFeePercentageBasisPoints = Math.round(parseFloat(auctionPieceDevFeePercentage) * 100);
      const tx = await factory.setDevFeeConfiguration(devFeePercentageBasisPoints, auctionPieceDevFeeRecipient);
      await tx.wait();

      setSuccess('Auction Sale Piece Factory dev fee configuration updated successfully!');
      await loadSpecificFactoryInfo('auction-sale-piece');
    } catch (err) {
      console.error('Error updating auction sale piece factory dev fee:', err);
      setError(`Failed to update dev fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAuctionSalePieceFactoryFakewhaleFee = async () => {
    if (!factoryAddresses?.factories.auctionSalePieceFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.auctionSalePieceFactory,
        factoryManagementABI,
        signer
      );

      const fakewhaleFeePercentageBasisPoints = Math.round(parseFloat(auctionPieceFakewhaleFeePercentage) * 100);
      const tx = await factory.setFakewhaleFeeConfiguration(fakewhaleFeePercentageBasisPoints, auctionPieceFakewhaleFeeRecipient);
      await tx.wait();

      setSuccess('Auction Sale Piece Factory fakewhale fee configuration updated successfully!');
      await loadSpecificFactoryInfo('auction-sale-piece');
    } catch (err) {
      console.error('Error updating auction sale piece factory fakewhale fee:', err);
      setError(`Failed to update fakewhale fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAuctionSalePieceReserveFactoryDevFee = async () => {
    if (!factoryAddresses?.factories.auctionSalePieceReserveFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.auctionSalePieceReserveFactory,
        factoryManagementABI,
        signer
      );

      const devFeePercentageBasisPoints = Math.round(parseFloat(auctionPieceReserveDevFeePercentage) * 100);
      const tx = await factory.setDevFeeConfiguration(devFeePercentageBasisPoints, auctionPieceReserveDevFeeRecipient);
      await tx.wait();

      setSuccess('Auction Sale Piece Reserve Factory dev fee configuration updated successfully!');
      await loadSpecificFactoryInfo('auction-sale-piece-reserve');
    } catch (err) {
      console.error('Error updating auction sale piece reserve factory dev fee:', err);
      setError(`Failed to update dev fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAuctionSalePieceReserveFactoryFakewhaleFee = async () => {
    if (!factoryAddresses?.factories.auctionSalePieceReserveFactory) return;

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed');
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      const factory = new ethers.Contract(
        factoryAddresses.factories.auctionSalePieceReserveFactory,
        factoryManagementABI,
        signer
      );

      const fakewhaleFeePercentageBasisPoints = Math.round(parseFloat(auctionPieceReserveFakewhaleFeePercentage) * 100);
      const tx = await factory.setFakewhaleFeeConfiguration(fakewhaleFeePercentageBasisPoints, auctionPieceReserveFakewhaleFeeRecipient);
      await tx.wait();

      setSuccess('Auction Sale Piece Reserve Factory fakewhale fee configuration updated successfully!');
      await loadSpecificFactoryInfo('auction-sale-piece-reserve');
    } catch (err) {
      console.error('Error updating auction sale piece reserve factory fakewhale fee:', err);
      setError(`Failed to update fakewhale fee: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  if (factoryLoading) {
    return (
      <div className="factory-manager">
        <h2>🏭 Factory Manager</h2>
        <p>Loading factory addresses...</p>
      </div>
    );
  }

  if (factoryError) {
    return (
      <div className="factory-manager">
        <h2>🏭 Factory Manager</h2>
        <p>Error loading factory addresses: {factoryError}</p>
      </div>
    );
  }

  if (!factoryAddresses?.factories.directSaleFactory || !factoryAddresses?.factories.auctionSaleFactory) {
    return (
      <div className="factory-manager">
        <h2>🏭 Factory Manager</h2>
        <p>Factory contracts not found. Please deploy the factory contracts first.</p>
      </div>
    );
  }

  if (!walletConnected) {
    return (
      <div className="factory-manager">
        <h2>🏭 Factory Manager</h2>
        <div className="error-message">
          <p>❌ Wallet not connected</p>
          <p>Please connect your wallet to manage factory configurations.</p>
        </div>
      </div>
    );
  }

  // Helper function to render a factory card
  const renderFactoryCard = (
    title: string,
    address: string | undefined,
    factoryInfo: any,
    devFeePercentage: string,
    setDevFeePercentage: (value: string) => void,
    devFeeRecipient: string,
    setDevFeeRecipient: (value: string) => void,
    fakewhaleFeePercentage: string,
    setFakewhaleFeePercentage: (value: string) => void,
    fakewhaleFeeRecipient: string,
    setFakewhaleFeeRecipient: (value: string) => void,
    updateDevFee: () => void,
    updateFakewhaleFee: () => void
  ) => {
    if (!address) return null;

    return (
      <div className="factory-card-full">
        <h3>{title}</h3>
        <p><strong>Address:</strong> <span className="contract-address">{address}</span></p>
        
        {factoryInfo && (
          <>
            <div className="current-config">
              <h4>Current Configuration</h4>
              <p><strong>Dev Fee:</strong> {factoryInfo.devFeePercentage / 100}% → <span className="contract-address">{factoryInfo.devFeeRecipient}</span></p>
              <p><strong>Fakewhale Fee:</strong> {factoryInfo.fakewhaleFeePercentage / 100}% → <span className="contract-address">{factoryInfo.fakewhaleFeeRecipient}</span></p>
              <p><strong>Owner:</strong> <span className="contract-address">{factoryInfo.owner}</span></p>
            </div>

            <div className="fee-management">
              <h4>Update Dev Fee Configuration</h4>
              <div className="form-group">
                <label>Dev Fee Percentage (0-10%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="10"
                  value={devFeePercentage}
                  onChange={(e) => setDevFeePercentage(e.target.value)}
                  disabled={isUpdating}
                />
              </div>
              <div className="form-group">
                <label>Dev Fee Recipient Address</label>
                <input
                  type="text"
                  value={devFeeRecipient}
                  onChange={(e) => setDevFeeRecipient(e.target.value)}
                  placeholder="0x..."
                  disabled={isUpdating}
                  className={devFeeRecipient && !ethers.isAddress(devFeeRecipient) ? 'error' : ''}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={updateDevFee}
                disabled={isUpdating || !devFeePercentage || !devFeeRecipient || !ethers.isAddress(devFeeRecipient)}
              >
                {isUpdating ? 'Updating...' : 'Update Dev Fee'}
              </button>

              <h4>Update Fakewhale Fee Configuration</h4>
              <div className="form-group">
                <label>Fakewhale Fee Percentage (0-10%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="10"
                  value={fakewhaleFeePercentage}
                  onChange={(e) => setFakewhaleFeePercentage(e.target.value)}
                  disabled={isUpdating}
                />
              </div>
              <div className="form-group">
                <label>Fakewhale Fee Recipient Address</label>
                <input
                  type="text"
                  value={fakewhaleFeeRecipient}
                  onChange={(e) => setFakewhaleFeeRecipient(e.target.value)}
                  placeholder="0x..."
                  disabled={isUpdating}
                  className={fakewhaleFeeRecipient && !ethers.isAddress(fakewhaleFeeRecipient) ? 'error' : ''}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={updateFakewhaleFee}
                disabled={isUpdating || !fakewhaleFeePercentage || !fakewhaleFeeRecipient || !ethers.isAddress(fakewhaleFeeRecipient)}
              >
                {isUpdating ? 'Updating...' : 'Update Fakewhale Fee'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="factory-manager">
      <h2>🏭 Factory Manager</h2>
      <p>Manage fee configurations for Direct Sale, Auction Sale, Direct Sale Piece, Auction Sale Piece, and Auction Sale Piece Reserve factories.</p>

      {success && (
        <div className="success-message">
          <p>✅ {success}</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      )}

      <div className="factory-tabs">
        <button
          className={`factory-tab ${activeTab === 'direct-sale' ? 'active' : ''}`}
          onClick={() => setActiveTab('direct-sale')}
        >
          Direct Sale
        </button>
        <button
          className={`factory-tab ${activeTab === 'auction-sale' ? 'active' : ''}`}
          onClick={() => setActiveTab('auction-sale')}
        >
          Auction Sale
        </button>
        <button
          className={`factory-tab ${activeTab === 'direct-sale-piece' ? 'active' : ''}`}
          onClick={() => setActiveTab('direct-sale-piece')}
          disabled={!factoryAddresses?.factories.directSalePieceFactory}
        >
          Direct Sale Piece
        </button>
        <button
          className={`factory-tab ${activeTab === 'auction-sale-piece' ? 'active' : ''}`}
          onClick={() => setActiveTab('auction-sale-piece')}
          disabled={!factoryAddresses?.factories.auctionSalePieceFactory}
        >
          Auction Sale Piece
        </button>
        <button
          className={`factory-tab ${activeTab === 'auction-sale-piece-reserve' ? 'active' : ''}`}
          onClick={() => setActiveTab('auction-sale-piece-reserve')}
          disabled={!factoryAddresses?.factories.auctionSalePieceReserveFactory}
        >
          Auction Sale Piece Reserve
        </button>
      </div>

      <div className="factory-tab-content">
        {activeTab === 'direct-sale' && renderFactoryCard(
          'Direct Sale Factory',
          factoryAddresses?.factories.directSaleFactory,
          directSaleFactoryInfo,
          directDevFeePercentage,
          setDirectDevFeePercentage,
          directDevFeeRecipient,
          setDirectDevFeeRecipient,
          directFakewhaleFeePercentage,
          setDirectFakewhaleFeePercentage,
          directFakewhaleFeeRecipient,
          setDirectFakewhaleFeeRecipient,
          updateDirectSaleFactoryDevFee,
          updateDirectSaleFactoryFakewhaleFee
        )}

        {activeTab === 'auction-sale' && renderFactoryCard(
          'Auction Sale Factory',
          factoryAddresses?.factories.auctionSaleFactory,
          auctionSaleFactoryInfo,
          auctionDevFeePercentage,
          setAuctionDevFeePercentage,
          auctionDevFeeRecipient,
          setAuctionDevFeeRecipient,
          auctionFakewhaleFeePercentage,
          setAuctionFakewhaleFeePercentage,
          auctionFakewhaleFeeRecipient,
          setAuctionFakewhaleFeeRecipient,
          updateAuctionSaleFactoryDevFee,
          updateAuctionSaleFactoryFakewhaleFee
        )}

        {activeTab === 'direct-sale-piece' && renderFactoryCard(
          'Direct Sale Piece Factory',
          factoryAddresses?.factories.directSalePieceFactory,
          directSalePieceFactoryInfo,
          directPieceDevFeePercentage,
          setDirectPieceDevFeePercentage,
          directPieceDevFeeRecipient,
          setDirectPieceDevFeeRecipient,
          directPieceFakewhaleFeePercentage,
          setDirectPieceFakewhaleFeePercentage,
          directPieceFakewhaleFeeRecipient,
          setDirectPieceFakewhaleFeeRecipient,
          updateDirectSalePieceFactoryDevFee,
          updateDirectSalePieceFactoryFakewhaleFee
        )}

        {activeTab === 'auction-sale-piece' && renderFactoryCard(
          'Auction Sale Piece Factory',
          factoryAddresses?.factories.auctionSalePieceFactory,
          auctionSalePieceFactoryInfo,
          auctionPieceDevFeePercentage,
          setAuctionPieceDevFeePercentage,
          auctionPieceDevFeeRecipient,
          setAuctionPieceDevFeeRecipient,
          auctionPieceFakewhaleFeePercentage,
          setAuctionPieceFakewhaleFeePercentage,
          auctionPieceFakewhaleFeeRecipient,
          setAuctionPieceFakewhaleFeeRecipient,
          updateAuctionSalePieceFactoryDevFee,
          updateAuctionSalePieceFactoryFakewhaleFee
        )}

        {activeTab === 'auction-sale-piece-reserve' && renderFactoryCard(
          'Auction Sale Piece Reserve Factory',
          factoryAddresses?.factories.auctionSalePieceReserveFactory,
          auctionSalePieceReserveFactoryInfo,
          auctionPieceReserveDevFeePercentage,
          setAuctionPieceReserveDevFeePercentage,
          auctionPieceReserveDevFeeRecipient,
          setAuctionPieceReserveDevFeeRecipient,
          auctionPieceReserveFakewhaleFeePercentage,
          setAuctionPieceReserveFakewhaleFeePercentage,
          auctionPieceReserveFakewhaleFeeRecipient,
          setAuctionPieceReserveFakewhaleFeeRecipient,
          updateAuctionSalePieceReserveFactoryDevFee,
          updateAuctionSalePieceReserveFactoryFakewhaleFee
        )}
      </div>

      <div className="factory-info">
        <h3>Factory Information</h3>
        <p><strong>Network:</strong> {factoryAddresses.network} (Chain ID: {factoryAddresses.chainId})</p>
        <p><strong>Deployer:</strong> {factoryAddresses.deployer}</p>
        <button 
          className="btn btn-secondary" 
          onClick={() => {
            // Clear the current factory info to force reload
            switch (activeTab) {
              case 'direct-sale':
                setDirectSaleFactoryInfo(null);
                break;
              case 'auction-sale':
                setAuctionSaleFactoryInfo(null);
                break;
              case 'direct-sale-piece':
                setDirectSalePieceFactoryInfo(null);
                break;
              case 'auction-sale-piece':
                setAuctionSalePieceFactoryInfo(null);
                break;
              case 'auction-sale-piece-reserve':
                setAuctionSalePieceReserveFactoryInfo(null);
                break;
            }
            loadSpecificFactoryInfo(activeTab);
          }}
          disabled={loading}
        >
          {loading ? 'Loading...' : '🔄 Refresh Factory Info'}
        </button>
      </div>
    </div>
  );
};

export default FactoryManager;

